import React, { useEffect, useState } from 'react';
import { getSiteScanLoginChoice } from '../../../services/scan.service';
import ScanLoginFields, { EMPTY_SCAN_LOGIN } from '../../settings/scan-network/ScanLoginFields';
import '../../settings/scan-network/scan-network.css';

const KEEP = '__keep';
const ONCE = '__once';
const RELAY_ROUTES = new Set(['BYO', 'PROVIDER_POOL']);

/**
 * Web scan form: how this scan logs in.
 * value: { scanLogin: null (saved login, or keep on an edit) | '' | 'none', scanLoginOnce: null | details }
 */
export default function ScanLoginOptions({ siteId, value, onChange, disabled, isEditMode, recurring, egressMode }) {
  const [choice, setChoice] = useState({ saved_method: '', can_enter: false });
  const once = value?.scanLoginOnce || null;

  useEffect(() => {
    setChoice({ saved_method: '', can_enter: false });
    if (!siteId) return undefined;
    let mounted = true;
    getSiteScanLoginChoice(siteId)
      .then((c) => mounted && setChoice({ saved_method: c?.saved_method || '', can_enter: Boolean(c?.can_enter) }))
      .catch(() => {});
    return () => { mounted = false; };
  }, [siteId]);

  const saved = Boolean(choice.saved_method);
  let selected = once ? ONCE : value?.scanLogin;
  if (selected === null || selected === undefined) selected = isEditMode ? KEEP : (saved ? '' : 'none');
  if (selected === '' && !saved) selected = 'none';

  const pick = (next) => {
    if (next === ONCE) onChange({ scanLogin: null, scanLoginOnce: once || { ...EMPTY_SCAN_LOGIN } });
    else onChange({ scanLogin: next === KEEP ? null : next, scanLoginOnce: null });
  };
  const usesLoginPage = once ? once.method === 'browser' : (selected === '' && choice.saved_method === 'browser');

  return (
    <div className="scan-network-site-settings">
      <label className="scan-network-field" htmlFor="wsm-scan-login">
        <span>Login</span>
        <select id="wsm-scan-login" className="scan-network-select" value={selected} disabled={disabled || !siteId}
          onChange={(e) => pick(e.target.value)}>
          {isEditMode ? <option value={KEEP}>Keep the current choice</option> : null}
          {saved ? <option value="">Use this site&apos;s saved login</option> : null}
          <option value="none">No login (public pages only)</option>
          {choice.can_enter ? <option value={ONCE}>{recurring ? 'Enter a login for this schedule' : 'Enter a login for this scan'}</option> : null}
        </select>
      </label>
      {once ? (
        <>
          <ScanLoginFields idPrefix="wsm-scan-login" value={once} disabled={disabled}
            onChange={(patch) => onChange({ scanLogin: null, scanLoginOnce: { ...once, ...patch } })} />
          <p className="deployment-section-desc">
            Use a test account: a logged-in scan submits forms and can change that account&apos;s data.
            {recurring ? ' Stored encrypted for as long as this schedule uses it.' : ' Stored encrypted and deleted when the scan ends.'}
          </p>
        </>
      ) : null}
      {usesLoginPage && RELAY_ROUTES.has(String(egressMode || '').toUpperCase()) ? (
        <div className="scan-network-alert scan-network-alert--error" role="alert">
          A login page needs a standard or dedicated IP route. On this route the scan will test the public pages only.
        </div>
      ) : null}
    </div>
  );
}
